import { useMemo } from "react";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import styles from "./MerchantIcon.module.scss";

export interface MerchantIconProps {
  name?: string | null;
  iconName?: string;
  size?: "s" | "l";
  /** Visual variant for the initials placeholder (no effect when a logo is available).
   *  - `"branded"` (default): theme-primary background with white text.
   *  - `"muted"`: light grey background with secondary text. */
  variant?: "branded" | "muted";
}

const MerchantIcon = ({
  name,
  iconName,
  size = "l",
  variant = "branded",
}: MerchantIconProps) => {
  const merchantName = name ?? "";
  const merchant = entities.merchants.useMerchant(merchantName);
  const initials = useMemo(
    () => merchantName.slice(0, 2).toUpperCase(),
    [merchantName]
  );
  const isMuted = variant === "muted" && !merchant?.logo_url;
  const containerClassName = useMemo(
    () =>
      cc(styles.container, {
        [styles.small]: size === "s",
        [styles.placeholder]: !merchant?.logo_url && !isMuted,
        [styles.placeholderMuted]: isMuted,
      }),
    [size, merchant?.logo_url, isMuted]
  );

  if (iconName) {
    return (
      <div className={containerClassName}>
        <span
          className={cc([
            `narmi-icon-${iconName}`,
            isMuted ? styles.iconMuted : styles.icon,
          ])}
        />
      </div>
    );
  }

  if (merchant?.logo_url) {
    return (
      <div className={containerClassName}>
        <img
          src={merchant.logo_url}
          alt={merchantName}
          className={styles.image}
        />
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <span className={isMuted ? styles.initialsMuted : styles.initials}>
        {initials}
      </span>
    </div>
  );
};

export default MerchantIcon;
